/* Boost recommendations in UpCart's "Check this out..." upsell section.

   UpCart owns the cart drawer (inside a shadow root) and asks
   window.upcartModifyListOfUpsells(list) which products to show. That hook must
   answer synchronously - a returned promise is ignored (measured) - so the Boost
   picks are fetched ahead of time whenever the cart loads or changes, cached per
   cart, and handed over on the next render. Until they land, UpCart shows its own.

   Boost logic, reused from the product-page widgets so it stays editable in the
   Boost admin:
     1. "Complete the ride" (bikes -> their battery, helmet, protection, tyres)
     2. "You may also need" (spares by bike size, plastics by manufacturer...)
   asked about the newest item in the cart. The first widget only has bike groups,
   so for anything else it answers nothing and the second is used.

   pg=product_page on the recommend call is what makes Boost evaluate those
   widgets' "if" conditions: the same request with pg=cart returns 0 (measured). */
(() => {
  const CFG = window.MotocrossBoostUpcartRecs;
  if (!CFG || !CFG.enabled) return;

  const ROOT = (CFG.rootUrl || '').replace(/\/+$/, '');
  const RECOMMEND_URL = 'https://services.mybcapps.com/discovery/recommend/';
  const ANALYTICS_URL = 'https://lambda.mybcapps.com/e';
  const ANON_CUSTOMER = '00000000-0000-0000-5000-000000000000';

  const state = {
    signature: null, // cart the cached picks were built for
    picks: [], // Shopify product objects, in Boost's order
    rid: null,
    wid: null,
    rt: null,
    building: null,
    rendered: null // signature last pushed to UpCart, to stop refresh loops
  };

  /* ---------------------------------------------------------------- config */

  /* Boost prints every widget's rules into the page. Walk for them by key rather
     than a fixed path, which moves with the app embed's block order. */
  let widgetsCache = null;
  function findWidgets() {
    if (widgetsCache) return widgetsCache;
    const roots = Object.keys(window).filter((k) => /^boost/i.test(k)).map((k) => window[k]);
    const seen = new WeakSet();
    const stack = roots.filter((r) => r && typeof r === 'object');
    let steps = 0;
    while (stack.length && steps < 20000) {
      steps += 1;
      const node = stack.pop();
      if (!node || typeof node !== 'object' || seen.has(node)) continue;
      seen.add(node);
      if (node.recommendationWidgets && typeof node.recommendationWidgets === 'object') {
        widgetsCache = node.recommendationWidgets['product-page'] || null;
        return widgetsCache;
      }
      for (const key of Object.keys(node)) {
        const value = node[key];
        if (value && typeof value === 'object') stack.push(value);
      }
    }
    return null;
  }

  /* --------------------------------------------------------------- request */

  async function recommend(wid, productId) {
    const widgets = findWidgets();
    const widget = widgets && widgets[wid];
    if (!widget || !widget.params) return null;
    const params = widget.params;
    const limit = Number(params.limit) || 12;
    const query = new URLSearchParams({
      recommendationType: params.recommendationType,
      limit: String(limit),
      shop: CFG.shop,
      wid,
      event_type: 'init',
      pg: 'product_page',
      layoutDisplay: params.layoutDisplay || 'carousel',
      return_all_currency_fields: 'false',
      locale: CFG.locale || 'en',
      currency_rate: '1.0',
      currency: CFG.currency,
      country: CFG.country || '',
      customer_id: CFG.customerId || ANON_CUSTOMER
    });
    const body = {
      shop: CFG.shop,
      recommendationType: params.recommendationType,
      limit,
      layoutDisplay: params.layoutDisplay || 'carousel',
      wid,
      productIds: [Number(productId)],
      collectionId: 0,
      customerId: CFG.customerId || ANON_CUSTOMER,
      locale: CFG.locale || 'en'
    };
    if (Array.isArray(params.rules) && params.rules.length) body.rules = params.rules;
    if (params.modelType) body.modelType = params.modelType;
    if (params.secondaryAlgorithm) body.secondaryAlgorithm = params.secondaryAlgorithm;

    const response = await fetch(`${RECOMMEND_URL}?${query}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error(`recommend ${response.status}`);
    const data = await response.json();
    return { products: data.products || [], rid: data.meta && data.meta.rid, rt: params.recommendationType, wid };
  }

  /* UpCart renders Shopify's own product JSON (/products/<handle>.js), not
     Boost's product shape, so each pick is re-read from Shopify. */
  function shopifyProduct(handle) {
    return fetch(`${ROOT}/products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
  }

  /* UpCart's own cart uses productId; Shopify's /cart.js uses product_id. */
  const productIdOf = (item) => (item && (item.product_id != null ? item.product_id : item.productId));

  function signatureOf(cart) {
    const items = (cart && cart.items) || [];
    return items.map((item) => `${productIdOf(item)}`).join(',');
  }

  async function build(cart) {
    const items = (cart && cart.items) || [];
    const signature = signatureOf(cart);
    if (!items.length) {
      Object.assign(state, { signature, picks: [], rid: null });
      return;
    }
    // Shopify lists the newest line first.
    const anchor = productIdOf(items[0]);
    const inCart = new Set(items.map((item) => String(productIdOf(item))));

    let answer = null;
    for (const wid of CFG.widgets) {
      try {
        const result = await recommend(wid, anchor);
        const usable = result && result.products.filter((p) => p && p.available && !inCart.has(String(p.id)));
        if (usable && usable.length) {
          answer = { ...result, products: usable };
          break;
        }
      } catch (error) {
        /* try the next widget */
      }
    }
    if (!answer) {
      Object.assign(state, { signature, picks: [], rid: null });
      return;
    }

    const wanted = answer.products.slice(0, CFG.maxItems);
    const picks = (await Promise.all(wanted.map((p) => shopifyProduct(p.handle)))).filter(
      (p) => p && p.available
    );
    Object.assign(state, { signature, picks, rid: answer.rid, wid: answer.wid, rt: answer.rt });
  }

  async function refreshFor(cart) {
    const signature = signatureOf(cart);
    if (state.building && state.building.signature === signature) return state.building.promise;
    const promise = build(cart).then(() => {
      // Re-render once for this cart; UpCart's refresh fires CART_UPDATED again,
      // which lands here with the same signature and stops.
      if (state.rendered !== signature && typeof window.upcartRefreshCart === 'function') {
        state.rendered = signature;
        window.upcartRefreshCart();
      }
    });
    state.building = { signature, promise };
    return promise;
  }

  /* ------------------------------------------------------------- the hook */

  window.upcartModifyListOfUpsells = (list) => {
    const cart = typeof window.upcartGetCart === 'function' ? window.upcartGetCart() : null;
    const current = cart && cart.items ? signatureOf(cart) : state.signature;
    if (state.picks.length && current === state.signature) return state.picks;
    return list; // UpCart's own picks until Boost's are ready, or if Boost has none
  };

  /* -------------------------------------------------------------- tracking */

  function uuid() {
    const helper = window.boostWidgetIntegration && window.boostWidgetIntegration.generateUuid;
    if (typeof helper === 'function') {
      try {
        const value = helper.call(window.boostWidgetIntegration);
        if (value) return String(value);
      } catch (error) {
        /* fall through */
      }
    }
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  function readBoostLocal(key) {
    try {
      const value = localStorage.getItem(key);
      return value ? String(value).replace(/^"|"$/g, '') : null;
    } catch (error) {
      return null;
    }
  }

  function sessionId() {
    const helper = window.boostWidgetIntegration && window.boostWidgetIntegration.getSessionId;
    if (typeof helper === 'function') {
      try {
        const value = helper.call(window.boostWidgetIntegration);
        if (value) return String(value).replace(/^"|"$/g, '');
      } catch (error) {
        /* fall through */
      }
    }
    return readBoostLocal('boostSdSessionId') || uuid();
  }

  /* Same payload as Boost's own recommendation events (verified on the Clothkits
     build): text/plain so sendBeacon needs no CORS preflight. */
  function track(userAction, productId, variantId, cartToken) {
    if (!state.rid) return;
    const payload = {
      tid: CFG.shop,
      qs: null,
      eid: uuid(),
      rid: state.rid,
      pid: productId ? String(productId) : '',
      t: new Date().toISOString(),
      u: userAction,
      a: 'recommend',
      r: document.referrer || '',
      sid: sessionId(),
      cid: CFG.customerId ? String(CFG.customerId) : ANON_CUSTOMER,
      pg: CFG.pageType || '',
      vid: variantId ? String(variantId) : '',
      bpids: '',
      wid: state.wid || '',
      rt: state.rt || '',
      actionBundle: false,
      bundle_info: {}
    };
    if (cartToken) {
      payload.ct = String(cartToken);
      const boostCartToken = readBoostLocal('boost_cart_token');
      if (boostCartToken) payload.bct = boostCartToken;
    }
    const blob = new Blob([JSON.stringify(payload)], { type: 'text/plain;charset=UTF-8' });
    if (navigator.sendBeacon && navigator.sendBeacon(ANALYTICS_URL, blob)) return;
    fetch(ANALYTICS_URL, { method: 'POST', headers: { 'Content-Type': 'text/plain;charset=UTF-8' }, body: JSON.stringify(payload), keepalive: true }).catch(() => {});
  }

  /* An upsell added from UpCart: find which of our picks it was. The event's
     detail shape is not documented, so look for a known product or variant id. */
  function idsIn(value, found = new Set(), depth = 0) {
    if (depth > 6 || value == null) return found;
    if (typeof value === 'number' || (typeof value === 'string' && /^\d{6,}$/.test(value))) found.add(String(value));
    else if (typeof value === 'object') Object.values(value).forEach((v) => idsIn(v, found, depth + 1));
    return found;
  }

  function onUpsellsAdded(detail) {
    const ids = idsIn(detail);
    state.picks.forEach((product) => {
      const variant = (product.variants || []).find((v) => ids.has(String(v.id)));
      if (variant || ids.has(String(product.id))) {
        // UpCart's cart carries no token: read it from Shopify after the add.
        fetch(`${ROOT}/cart.js`)
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
          .then((cart) => track('add_to_cart', product.id, variant ? variant.id : null, cart && cart.token));
      }
    });
  }

  function onShadowClick(event) {
    const link = event.composedPath().find((el) => el && el.tagName === 'A' && /\/products\//.test(el.getAttribute('href') || ''));
    if (!link) return;
    const handle = (link.getAttribute('href').match(/\/products\/([^/?#]+)/) || [])[1];
    const product = state.picks.find((p) => p.handle === handle);
    if (product) track('view_product', product.id, null, null);
  }

  /* upcartGetCart() may hand back the cart or a promise of it; fall back to /cart.js. */
  function currentCart() {
    let value = null;
    try {
      value = typeof window.upcartGetCart === 'function' ? window.upcartGetCart() : null;
    } catch (error) {
      value = null;
    }
    return Promise.resolve(value)
      .then((cart) => (cart && cart.items ? cart : fetch(`${ROOT}/cart.js`).then((r) => (r.ok ? r.json() : null))))
      .catch(() => null);
  }

  /* ------------------------------------------------------------------ wire */

  function start() {
    if (typeof window.upcartSubscribeCartUpdated !== 'function') return false;
    window.upcartSubscribeCartLoaded((detail) => refreshFor(detail && detail.cart));
    window.upcartSubscribeCartUpdated((detail) => refreshFor(detail && detail.cart));
    if (typeof window.upcartSubscribeUpsellsAddedToCart === 'function') {
      window.upcartSubscribeUpsellsAddedToCart(onUpsellsAdded);
    }
    const host = document.getElementById('upCart');
    if (host && host.shadowRoot) host.shadowRoot.addEventListener('click', onShadowClick, true);
    // A cart that already exists on page load.
    currentCart().then((cart) => cart && cart.items && refreshFor(cart));
    return true;
  }

  // UpCart's API appears once its bundle has loaded.
  let attempts = 0;
  const timer = setInterval(() => {
    if (start() || ++attempts > 100) clearInterval(timer);
  }, 200);
})();
